import {
  DIMENSION_LABELS,
  type AnalyticsGranularity,
  type AnalyticsSeries,
  type CategoryDatum,
  type Distribution,
  type FlowReport,
  type OnTimeSplit,
  type QualityReport,
  type StaffRow,
  type SupplyRow,
} from '#shared/types';
import { formatHours, formatPercent } from './format';

/**
 * The small decisions the analytics pages make about a report.
 *
 * Kept out of the components so each is a function of the payload alone, and
 * so the thresholds (six slices, ten consumers, 1.3× the median) live in one
 * readable place rather than inside JSX.
 */

/** The dimension's display name, falling back to its key for one not yet labelled. */
export function dimensionLabel(by: string): string {
  return DIMENSION_LABELS[by] ?? by;
}

export function comparisonLabel(compare: string): string {
  return compare === 'year' ? 'Same period last year' : 'Previous period';
}

const GRANULARITY_LABELS: Record<AnalyticsGranularity, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

export function granularityLabel(granularity: AnalyticsGranularity): string {
  return GRANULARITY_LABELS[granularity];
}

/** True when no bucket in the series recorded anything. */
export function isSeriesEmpty(series: AnalyticsSeries): boolean {
  return series.points.every((point) => !point.value);
}

export function sumValues(rows: readonly CategoryDatum[]): number {
  return rows.reduce((sum, row) => sum + row.value, 0);
}

/**
 * A ring for a mix of a handful of things, bars for a ranking of many. Six is
 * where a donut stops being readable.
 */
export function showsAsDonut(rows: readonly CategoryDatum[]): boolean {
  return rows.length <= 6;
}

/** A chart height that grows with its categories, never below `minimum`. */
export function heightForRows(count: number, perRow: number, minimum: number): number {
  return Math.max(minimum, count * perRow);
}

/* --- Flow ---------------------------------------------------------------- */

export function isFlowEmpty(report: FlowReport): boolean {
  return report.total_turnaround.n === 0 && report.backlog.every((row) => !row.value);
}

/**
 * True when the mean sits well above the median — the sign that a few slow
 * reports are pulling it, which is the only thing a mean is good for on a
 * skewed measure.
 */
export function isMeanSkewed(spread: Distribution): boolean {
  return spread.mean !== null && spread.p50 !== null && spread.mean > spread.p50 * 1.3;
}

export function formatOptionalHours(hours: number | null): string {
  return hours === null ? '—' : formatHours(hours);
}

export function formatOnTimeRate(rate: number | null): string {
  return rate === null ? 'No commitment' : formatPercent(rate);
}

/* --- Overview ------------------------------------------------------------ */

export function turnaroundSubtitle(split: OnTimeSplit): string {
  return split.no_commitment > 0
    ? `${split.no_commitment} report(s) had no committed turnaround and are not counted`
    : 'Against each test’s committed turnaround';
}

export function onTimeCaption(split: OnTimeSplit): string {
  return split.rate === null
    ? 'No report in this period had a committed turnaround to be measured against.'
    : `${formatPercent(split.rate)} met their committed turnaround.`;
}

/* --- Quality ------------------------------------------------------------- */

type QualityFlags = QualityReport['flags'];

/** Results a reference range could speak to: within, above or below. */
export function judgeableCount(flags: QualityFlags): number {
  return flags.normal + flags.high + flags.low;
}

export function isQualityEmpty(report: QualityReport): boolean {
  return judgeableCount(report.flags) === 0 && (report.flags.not_judgeable ?? 0) === 0;
}

/** The donut's centre: share outside range, over judgeable results only. */
export function formatOutsideRangeShare(flags: QualityFlags): string {
  const judgeable = judgeableCount(flags);
  return judgeable === 0 ? '—' : formatPercent((flags.high + flags.low) / judgeable);
}

/* --- Staff --------------------------------------------------------------- */

/**
 * "Too few" rather than a number: four reports is not a performance record,
 * and printing a median from them invites a comparison the data cannot
 * support — about a person.
 */
export function formatStaffMedian(row: Pick<StaffRow, 'p50_hours' | 'too_few'>): string {
  if (row.p50_hours !== null) return formatHours(row.p50_hours);
  return row.too_few ? 'Too few' : '—';
}

/* --- Supply -------------------------------------------------------------- */

/** The ten heaviest users of stock that were actually used in the period. */
export function topConsumers(rows: readonly SupplyRow[]): SupplyRow[] {
  return rows.filter((row) => row.consumed_base > 0).slice(0, 10);
}
